// AppHeader.js
import React, {useEffect, useState, useRef} from 'react';
import { styled  } from '@mui/material/styles';
import { Box } from '@mui/system';
import { Button, IconButton, Popover } from '@mui/material';
import ForumIcon from '@mui/icons-material/Forum';
import ModeOfTravelIcon from '@mui/icons-material/ModeOfTravel';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import KeyIcon from '@mui/icons-material/Key';
import LogoutIcon from '@mui/icons-material/Logout';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import PasswordIcon from '@mui/icons-material/Password';
import EditIcon from '@mui/icons-material/Edit';
import { AppStyle, AppWord, AppObject, AppMenuUrl } from '../../value/AppConst';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { userInfoRepo, menuTreeRepo, clearReposForNewSession } from '../../value/AppModel';
import HomeplusTextLogo from '../../asset/image/homeplus_no_line.svg';
import PlusLogo from '../../asset/image/plusmark.svg';
import UserSessionInfo from '../../value/UserSessionInfo';
import { LOGOUT_NEW_TOP } from '../login/AppLoginGql';
import { useAlertSetter, useClientSize } from '../../api/app-hook';
import { LIST_MY_MENU_INFO, makeMenuTree } from '../menu_info/MenuInfoGql';
import { MenuHardWorking, MenuNotImplemented } from '../menu_info/MenuInfoTree';
import ValueMisc from '../../value/ValueMisc';


// ------------------------------------------
const HeaderHeight = AppStyle.AppHeaderHeight;
const LogoHeight = HeaderHeight - 24;
const PlusLogoHeight = HeaderHeight - 14;
const MaxWidthForLetterLogo = 1130;
const MaxWidthForBBS = 980;
const MaxWidthForUserInfo = 950;

const HeaderBox = styled(Box)({
    position: 'fixed', top: 0, right: 0, left: 0, height: HeaderHeight,
    backgroundColor: AppStyle.HeaderBackgroundColor, color: '#fff', fontSize: '0.9rem',
    display: 'flex', alignItems: 'center',
});

const LogoBox = styled(Box)({display: 'flex', alignItems: 'center', padding: '0px 10px 0px 30px',flexGrow:1});
const MenuBox = styled(Box)({display: 'flex', alignItems: 'center', flexGrow: 3, });
const HotButtonBox = styled(Box)({display: 'flex', alignItems: 'center', padding: '0px 10px 0px 15px'});
const UserBox = styled(Box)({
    display: 'flex', alignItems: 'center', padding: '0px 15px 0px 10px', cursor: 'pointer',
    fontSize: '0.9rem',
    '&:hover': {color: 'yellow'}
});

const UserPopBox = styled(Box)({
    padding:15, backgroundColor: '#f0f0f0', 
    border: AppStyle.BorderCCC, borderRadius: 5,
});

const UserPopLine = styled(Box)({
    display: 'flex', alignItems: 'center', padding: '5px 0px 5px 0px',
});


// Top menu list (no sub menus)
const TopMenuBox = styled(Box)({
    display: 'flex', alignItems: 'end'
});

const TopMenuButtonBox = styled(Box)({
    display: 'flex', alignItems: 'center', padding: '0px 5px 0px 5px', borderRight: AppStyle.TopMenuBorder,
    '&:last-of-type': {borderRight: 'none'}
});

const TopMenuButton = styled(Button)({
    textTransform: 'none !important', fontSize: '0.85rem', color: '#fff',
    textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden',
    '&:hover': {color: '#ff0'}
});

// for modal background
const SubMenuBackground = styled(Box)({
    position: 'fixed', top: AppStyle.AppHeaderHeight, right: 0, bottom: 0, left: 0,
    backgroundColor: 'rgba(0,0,0,0.01)',
    zIndex: 99990,
});

const SubMenuBox = styled(Box)({
    backgroundColor: '#fff', color: 'black', border: AppStyle.BorderEEE, borderRadius: 2,
    position: 'absolute',
    boxShadow: '2px 2px 5px #ddd', display: 'inline-block'
});

const SubMenuLine = styled(Box)({
    padding:'7px 12px 7px 12px', borderBottom: AppStyle.BorderCCC, cursor: 'pointer',
    zIndex: 99992,
    '&:hover': {backgroundColor: '#dff'},
    '&:last-of-type': {borderBottom: 'none'}
});

// authClassInt에 따라 점선을 표시.
const AuthMarkDevider = styled(Box)({display: 'flex', height: 1, width: '100%', marginBottom: 5});
const AuthMarkLine = styled(Box)({
    height:1, backgroundColor: '#d0d0f0', flexGrow: 1, marginRight: 2,
    '&:hover': {backgroundColor: AppStyle.PrimaryRGB},
    '&:last-of-type': {marginRight: 0}
});

const BbsButton = styled(Button)({
    color: '#fff',
    '&:hover': {color: '#ff0'}
});

export default function AppHeader({
    onMenuSelected,
    onClickHomeLogo,
    onClickGoBbs,
    onRequestOpenMaskDc,
    onRequestOpenEditMyInfo,
    onRequestOpenChangeMyPassword
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const [userInfoAnchorEl, setUserInfoAnchorEl] = useState(null);
    const addAlert = useAlertSetter();
    const [menus, setMenus] = useState([]);
    const [topMenuNow, setTopMenuNow] = useState({});
    const clientSize = useClientSize();
    const startRef = useRef(0);
    const [getMyMenuList, responseMyMenuList] = useLazyQuery(LIST_MY_MENU_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data) => {onGetMyMenuList(data)},
        onError: (error) => addAlert({title:"메뉴 정보 조회 중 오류", message:[error.message]})
    });

    useEffect(() => {
        if(startRef.current === 0) {
            getMyMenuList();
            startRef.current = 1;
        }
    }, []);

    const onGetMyMenuList = (data) => {
        if(data.menuInfoListMine) {
            const menuTree = makeMenuTree(data.menuInfoListMine);
            setMenus(menuTree);
            menuTreeRepo(menuTree);
        }
    };

    const [logoutNewTop, responseLogout] = useLazyQuery(LOGOUT_NEW_TOP, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteLogout(data, option)},
        onError: (error) => addAlert({title:"로그아웃 중 오류", message:[error.message]})
    })

    const onCompleteLogout = (data, option) => {
        if(data.logoutNewTop) {
            const sessionInfo = UserSessionInfo.initFromUserObject(data.logoutNewTop);
            userInfoRepo(sessionInfo);
            clearReposForNewSession(); // clear all repos
        }
        else addAlert({title:"로그아웃 중 오류", message:['알 수 없는 오류(GraphQL)']})
    };

    const open = Boolean(userInfoAnchorEl);
    const idForOpen = open ? 'user_info_pop' : undefined;


    const onTopMenuSelected = (e, menu) => {
        // Get the position of this menu button
        const rect = e.currentTarget.getBoundingClientRect();
        setTopMenuNow({menu, left: rect.left});
        // stop propagation
        e.stopPropagation();
    };

    const onMouseEnterTopMenu = (e, menu) => {
        if(Boolean(topMenuNow.menu)) {
            if(topMenuNow.menu.menuId === menu.menuId) return;
            const rect = e.currentTarget.getBoundingClientRect();
            setTopMenuNow({menu, left: rect.left});
        }
    };

    const onCloseSubmenu = () => {
        setTopMenuNow({});
    };

    const onClickHeader = () => {
        if(Boolean(topMenuNow.menu)) {
            onCloseSubmenu();
        }
    };

    const popNewWindow = () => {
        const url = ValueMisc.getUrlBase();
        window.open(url, '_blank');
    };

    const onClickMenu = (menu, parent) => {
        onCloseSubmenu();
        if(menu.menuUrl===AppMenuUrl.MenuReload) {
            getMyMenuList();
            return;
        }
        if(menu.menuUrl===AppMenuUrl.NewWin) {
            popNewWindow();
            return;
        }
        onMenuSelected(menu, parent);
    };

    const renderSubMenuBox = () => {
        if(topMenuNow.menu) {
            return(
                <SubMenuBackground onClick={onCloseSubmenu}>
                    <SubMenuBox style={{top:0, left: topMenuNow.left-10}}>
                        {
                            topMenuNow.menu.children.map((subMenu, index) => {
                                return(
                                    <React.Fragment key={index}>
                                    <SubMenuLine
                                        onClick={() => onClickMenu(subMenu)}
                                    >
                                        {
                                            MenuHardWorking.includes(subMenu.menuUrl) ?
                                            <span style={{color:'#f00'}}>{subMenu.menuName}</span> :
                                            MenuNotImplemented.includes(subMenu.menuUrl) ?
                                            <span style={{color:'#999'}}>{subMenu.menuName}</span> :
                                            <span>{subMenu.menuName}</span>
                                        }
                                    </SubMenuLine>
                                    {
                                        subMenu.children ?
                                        subMenu.children.map((ssMenu, ssIndex) => {
                                            return(
                                                <SubMenuLine key={index*100+ssIndex} style={{paddingLeft:15}}
                                                    onClick={() => onClickMenu(ssMenu, subMenu)}
                                                >
                                                    <ArrowRightIcon
                                                        style={{fontSize:16, marginRight:10, color:'#ccc'}}
                                                    />
                                                    {
                                                        MenuHardWorking.includes(ssMenu.menuUrl) ?
                                                        <span style={{color:'#f00'}}>{ssMenu.menuName}</span> :
                                                        MenuNotImplemented.includes(ssMenu.menuUrl) ?
                                                        <span style={{color:'#999'}}>{ssMenu.menuName}</span> :
                                                        <span>{ssMenu.menuName}</span>
                                                    }
                                                </SubMenuLine>
                                            );
                                        }) : null
                                    }
                                    </React.Fragment>
                                );
                            })
                        }
                    </SubMenuBox>
                </SubMenuBackground>
            );
        }
        return null;
    };

    const renderTopMenu = () => {
        if(responseMyMenuList.loading) {
            return <TopMenuBox>Loading......</TopMenuBox>
        }
        return(
            <TopMenuBox>
                {
                    menus.map((menu, index) => {
                        if(menu.children && menu.children.length > 0)
                            return(
                                <TopMenuButtonBox key={index}>
                                    <TopMenuButton
                                        onClick={(e) => onTopMenuSelected(e, menu)}
                                        onMouseEnter={(e) => onMouseEnterTopMenu(e, menu)}
                                    >
                                        {menu.menuName}
                                    </TopMenuButton>
                                </TopMenuButtonBox>
                            );
                        return null;
                    })
                }
                {renderSubMenuBox()}
            </TopMenuBox>
        );
    };

    const renderAuthLine = () => {
        if(sessionInfo.user.authClassInt === 0) return null;
        return(
            <AuthMarkDevider>
                {
                    Array(sessionInfo.user.authClassInt).fill(0).map((_, idx) => {
                        return(
                            <AuthMarkLine key={idx}/>
                        );
                    })
                }
            </AuthMarkDevider>
        )
    };

    // show name, dscName, button to change dc if applicable(allDcYn=='Y').
    const renderUserPop = () => {
        return(
            <Popover
                open={open}
                id={idForOpen}
                anchorEl={userInfoAnchorEl}
                onClose={() => setUserInfoAnchorEl(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'left', }}
                transformOrigin={{ vertical: 'top', horizontal: 'left', }}
            >
                <UserPopBox onMouseLeave={()=>setUserInfoAnchorEl(null)}>
                    <UserPopLine>
                        <PersonOutlineIcon style={{marginRight:20}}/>
                        {sessionInfo.user.userName}
                    </UserPopLine>
                    <UserPopLine style={{fontSize:'0.85rem'}}>
                        <ModeOfTravelIcon style={{marginRight:20}} />
                        {sessionInfo.user.dscNameAs}
                    </UserPopLine>
                    {renderAuthLine()}
                    {
                        sessionInfo.user.allDcYn === 'Y' &&
                        <UserPopLine>
                            <Button
                                variant={AppStyle.mui.contained}
                                size={AppWord.SMALL}
                                fullWidth
                                startIcon={<KeyIcon />}
                                color={AppStyle.mui.success}
                                onClick={()=>onRequestOpenMaskDc()}
                            >
                                DC 전환
                            </Button>
                        </UserPopLine>
                    }
                    <UserPopLine>
                        <Button
                            variant={AppStyle.mui.contained}
                            size={AppWord.SMALL}
                            fullWidth
                            startIcon={<EditIcon />}
                            color={AppStyle.mui.primary}
                            onClick={()=>onRequestOpenEditMyInfo()}
                        >내 정보 수정</Button>
                    </UserPopLine>
                    <UserPopLine>
                        <Button
                            variant={AppStyle.mui.contained}
                            size={AppWord.SMALL}
                            fullWidth
                            startIcon={<PasswordIcon />}
                            color={AppStyle.mui.warning}
                            onClick={()=>onRequestOpenChangeMyPassword()}
                        >내 암호 변경</Button>
                    </UserPopLine>
                    <UserPopLine>
                        <Button
                            variant={AppStyle.mui.contained}
                            size={AppWord.SMALL}
                            fullWidth
                            startIcon={<LogoutIcon />}
                            color={AppStyle.mui.secondary}
                            onClick={()=>logoutNewTop()}
                        >
                            로그아웃
                        </Button>
                    </UserPopLine>
                </UserPopBox>
            </Popover>
        );
    };

    return(
        <HeaderBox onClick={onClickHeader}>
            <LogoBox>
                {
                    clientSize.width >= MaxWidthForLetterLogo ?
                    <img src={HomeplusTextLogo}
                        alt='Homeplus'
                        height={LogoHeight}
                        style={{cursor:'pointer'}}
                        onClick={onClickHomeLogo}
                    />
                    :
                    <img src={PlusLogo}
                        alt='Homeplus'
                        height={PlusLogoHeight}
                        style={{cursor:'pointer'}}
                        onClick={onClickHomeLogo}
                    />
                }
            </LogoBox>
            <MenuBox>
                {renderTopMenu()}
            </MenuBox>
            <HotButtonBox>
                {
                    clientSize.width >= MaxWidthForBBS ?
                    <BbsButton
                        variant={AppStyle.mui.text}
                        startIcon={<ForumIcon />}
                        size={AppWord.SMALL}
                        onClick={onClickGoBbs}
                    >
                        게시판
                    </BbsButton>
                    :
                    <IconButton
                        size={AppWord.SMALL}
                        onClick={onClickGoBbs}
                        style={{color:'#fff'}}
                    >
                        <ForumIcon />
                    </IconButton>
                }
            </HotButtonBox>
            <UserBox onClick={(e)=>setUserInfoAnchorEl(e.currentTarget)}>
                <PersonOutlineIcon style={{marginRight:5}}/>
                {
                    clientSize.width >= MaxWidthForUserInfo &&
                    <Box>
                        <span style={{fontWeight:'bold'}}>{sessionInfo.user.userName}</span>
                        <Box style={{fontSize:'0.8rem'}}>
                            {sessionInfo.user.dscNameAs}
                        </Box>
                    </Box>
                }
            </UserBox>
            {renderUserPop()}
        </HeaderBox>
    );
}
